import * as signalR from "@microsoft/signalr";
import * as msgpack from "@microsoft/signalr-protocol-msgpack";
import { getToken } from "@/utils/token";
import { getFullImageUrl } from "@/components/ImageShow.vue";

export default class baseSignalRService {
    constructor() {
        this.url = null;
        this.messagePackHubProtocol = null;
        this.connection = null;
        this.isStarting = false;
        this.pendingListeners = []; // 存储在连接初始化前注册的监听器
        this.pendingSend = []; // 存储在连接初始化前发送的消息
    }

    async send(methodName, ...args) {
        if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
            await this.connection.send(methodName, ...args);
            return;
        }
        this.pendingSend.push({ methodName, args });
    }

    /**
     * 初始化 SignalR 连接
     * @param {string} url Hub 的相对路径，可选。如果提供则覆盖实例的默认 URL。
     */
    async init(url) {
        if (url) {
            this.url = url;
        }
        if (!this.url) {
            console.error("SignalR URL is required.");
            return;
        }
        if (this.connection) {
            console.log("SignalR already initialized.");
            return;
        }
        console.log("Initializing SignalR at:", getFullImageUrl(this.url));

        let builder = new signalR.HubConnectionBuilder()
            .withUrl(getFullImageUrl(this.url), {
                accessTokenFactory: () => getToken(),
            })
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.Warning);
        if (this.messagePackHubProtocol) {
            builder = builder.withHubProtocol(new msgpack.MessagePackHubProtocol());
        }
        this.connection = builder.build();

        // 监听连接关闭，如果不是主动停止则尝试重新启动
        this.connection.onclose((error) => {
            console.log("SignalR Connection Closed: ", error);
            // 如果连接对象还存在（说明不是调用 stop() 主动销毁的），尝试重新开始
            if (this.connection) {
                this.start();
            }
        });

        this.connection.onreconnecting((error) => {
            console.log("SignalR Connection Reconnecting: ", error);
        });

        this.connection.onreconnected((connectionId) => {
            console.log("SignalR Connection Reconnected: ", connectionId);
        });

        // 注册所有挂起的监听器
        if (this.pendingListeners.length > 0) {
            console.log(`Registering ${this.pendingListeners.length} pending listeners.`);
            this.pendingListeners.forEach(({ methodName, callback }) => {
                this.connection.on(methodName, callback);
            });
            this.pendingListeners = [];
        }
        await this.start();
        // 发送缓存的消息
        if (this.pendingSend.length > 0) {
            console.log(`Resending ${this.pendingSend.length} pending messages.`);
            for (const { methodName, args } of this.pendingSend) {
                await this.connection.send(methodName, ...args);
            }
            this.pendingSend = []; // 发送完成后清空缓存
        }
    }

    async start() {
        if (this.isStarting) return;
        this.isStarting = true;

        try {
            if (this.connection && this.connection.state === signalR.HubConnectionState.Disconnected) {
                await this.connection.start();
                console.log("SignalR Connected.");
            }
        } catch (err) {
            console.error("SignalR Connection Error: ", err);
            // 失败后 5 秒重连
            setTimeout(() => {
                this.isStarting = false;
                this.start();
            }, 5000);
        } finally {
            this.isStarting = false;
        }
    }

    /**
     * 监听服务器推送的消息
     * @param {string} methodName 方法名
     * @param {function} callback 回调函数
     */
    on(methodName, callback) {
        if (this.connection) {
            this.connection.on(methodName, callback);
        } else {
            // 如果连接还没初始化，先存起来
            console.log(`SignalR not initialized. Queuing listener for ${methodName}`);
            this.pendingListeners.push({ methodName, callback });
        }
    }

    /**
     * 取消监听
     * @param {string} methodName 方法名
     * @param {function} callback 可选，特定的回调函数
     */
    off(methodName, callback) {
        if (this.connection) {
            if (callback) {
                this.connection.off(methodName, callback);
            } else {
                this.connection.off(methodName);
            }
        } else {
            // 如果在挂起队列中，则从队列移除
            this.pendingListeners = this.pendingListeners.filter(
                (l) => !(l.methodName === methodName && (!callback || l.callback === callback))
            );
        }
    }

    /**
     * 停止连接
     */
    async stop() {
        if (this.connection) {
            const connection = this.connection;
            this.connection = null;
            await connection.stop();
            console.log("SignalR Stopped.");
        }
    }
}
